/**
 * Workgate / Carat 応募ペイロードの組み立て。
 *
 * WorkgateDialog から切り出した純関数。localStorage 由来のデータだけを見るので
 * コンポーネントに依存しない。不足項目は missingFields に積んで呼び出し側が出す。
 */
import {
  wgBuildEducation,
  wgBuildIntroduction,
  wgBuildJobHistory,
  wgFormatBirth,
  wgNormalizeDigits,
  wgParsePrefecture
} from '@/utils/workgate-format'

export interface StoredApplySource {
  basic?: Record<string, any> | null
  formatData?: Record<string, any> | null
  resume?: Record<string, any> | null
  /** basic.email が空のときの代替（Firebase のログインメール） */
  fallbackEmail?: string
}

export function buildWorkgateApplyPayload(
  stored: StoredApplySource
): Record<string, any> {
  const basic = stored.basic || {}
  const email = basic.email || stored.fallbackEmail || ''
  const lastname = basic.lastName || ''
  const firstname = basic.firstName || ''
  const lastnameKana = basic.lastNameFurigana || ''
  const firstnameKana = basic.firstNameFurigana || ''
  const birth = wgFormatBirth(basic.birthYear, basic.birthMonth, basic.birthDay)
  const pref = wgParsePrefecture(basic.address1 || '')
  const prefCode = pref.code
  const address1 = pref.name
    ? (basic.address1 || '').replace(pref.name, '')
    : basic.address1 || ''
  const address2 = basic.address2 || ''
  const zipCode = wgNormalizeDigits(basic.postNumber || '')
  const telephone = wgNormalizeDigits(
    (basic.tel1 || '') + (basic.tel2 || '') + (basic.tel3 || '')
  )
  const gender = Number(basic.sex || 0) === 1 ? 1 : 2
  const formatData = stored.formatData || {}
  const resume = stored.resume || {}
  const jobHistory = wgBuildJobHistory(
    basic.jobHistories ? basic : formatData.jobHistories ? formatData : resume
  )
  const education = wgBuildEducation(
    basic.educationalHistories
      ? basic
      : formatData.educationalHistories
        ? formatData
        : resume
  )
  const introduction = wgBuildIntroduction(
    basic.licenseHistories
      ? basic
      : formatData.licenseHistories
        ? formatData
        : resume.licenseHistories
          ? resume
          : {}
  )
  const currentJob = jobHistory ? null : { select: 11, textarea: '' }

  const missingFields = []
  if (!email) missingFields.push('メールアドレス')
  if (!lastname) missingFields.push('姓')
  if (!firstname) missingFields.push('名')
  if (!lastnameKana) missingFields.push('セイ')
  if (!firstnameKana) missingFields.push('メイ')
  if (!birth) missingFields.push('生年月日')
  if (!prefCode) missingFields.push('住所(都道府県)')
  if (!address1) missingFields.push('住所')
  if (!telephone) missingFields.push('電話番号')
  if (!education) missingFields.push('学歴')

  return {
    email,
    lastname,
    firstname,
    lastname_kana: lastnameKana,
    firstname_kana: firstnameKana,
    birth,
    gender,
    zip_code: zipCode,
    pref_code: prefCode,
    address1,
    address2,
    telephone,
    education,
    job_history: jobHistory,
    current_job: currentJob,
    introduction,
    environment: {
      user_agent: navigator.userAgent || ''
    },
    missingFields
  }
}

/**
 * ダイアログを出す前の必須項目チェック。
 *
 * ここで落ちる会員は応募まで到達できないため、ポップアップ自体を出さない。
 * 電話番号は tel1〜tel3 の連結から全角込みで数字以外を落として判定する。
 */
export function hasRequiredBasicInfo(
  basicData: Record<string, any> | null | undefined,
  fallbackEmail = ''
): boolean {
  const basic = basicData || {}
  const email = basic.email || fallbackEmail || ''
  const telephone = (
    String(basic.tel1 || '') +
    String(basic.tel2 || '') +
    String(basic.tel3 || '')
  ).replace(/[^0-9０-９]/g, '')
  return !!(
    email &&
    basic.lastName &&
    basic.firstName &&
    basic.lastNameFurigana &&
    basic.firstNameFurigana &&
    basic.birthYear &&
    basic.birthMonth &&
    basic.birthDay &&
    basic.address1 &&
    telephone
  )
}
